import { messages } from '../../../utils/messages'
import { useDispatch, useSelector } from 'react-redux'
import { action, StyledMenu } from '../../../utils/utils'
import { Box, Button, Badge, Snackbar } from '@mui/material'
import { getAllProrithmDevices } from '../../../utils/services'
import { useProrithmWebSocket } from '../ProrithmWebSocketContext'
import { DevicesList, ProrithmNotification } from '../../../interfaces'
import { constants, prorithmMessageTypes } from '../../../utils/constants'
import {
  useRef,
  useState,
  useEffect,
  MouseEvent,
  ReactElement,
  SyntheticEvent,
} from 'react'
import {
  setDeviceList,
  setSelectedDevice,
  setIsDeviceClicked,
  setSelectedCategory,
  setSelectedDeviceIndex,
  removeProrithmNotification,
  markProrithmNotificationAsRead,
} from '../../../redux/actions'
import useSound from 'use-sound'
import NotificationItem from './NotificationItem'
import NotificationsIcon from '@mui/icons-material/Notifications'
import './ProrithmNotifications.css'

const ProrithmNotifications = (): ReactElement => {
  const alertSound = require('../../../assets/AlertSound/alertSound.mp3')
  const [play] = useSound(alertSound, { volume: 0.2 })
  const { lastMessage } = useProrithmWebSocket()
  const dispatch = useDispatch()

  const prorithmNotifications = useSelector(
    (state: any) => state.prorithmNotifications
  )

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [unreadCount, setUnreadCount] = useState<number>(0)
  const [message, setMessage] = useState<string>('')
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false)

  const prevNotificationLength = useRef(prorithmNotifications?.length || 0)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const open = Boolean(anchorEl)

  const handleNotificationClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    event.preventDefault()
    setAnchorEl(event.currentTarget)
  }

  const handleNotificationClose = (
    _event: SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === constants?.clickaway) {
      return
    }
    setOpenSnackbar(false)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const removeNotification = (e: React.MouseEvent, index: number) => {
    e.stopPropagation()
    dispatch(removeProrithmNotification(index))
  }

  const handleNotificationItemClick = async (
    notification: ProrithmNotification,
    index: number
  ) => {
    dispatch(markProrithmNotificationAsRead(index))

    if (prorithmMessageTypes?.includes(notification?.messageType)) {
      const macId = notification?.messageContent?.macId
      const category = notification?.messageContent?.category

      dispatch(setSelectedCategory(category))
      try {
        const allDevices = await getAllProrithmDevices(true, category)
        if (allDevices?.success) {
          const withIndicator = allDevices?.data?.map(
            (item: DevicesList, index: number) => {
              return {
                ...item,
                indicator: constants?.offline,
                sNo: index + 1,
              }
            }
          )

          const targetDeviceIndex = withIndicator?.findIndex(
            (device: DevicesList) => device?.deviceId === macId
          )
          const targetDevice = withIndicator?.[targetDeviceIndex] ?? null

          dispatch(setDeviceList(withIndicator))
          dispatch(setSelectedDevice(targetDevice))
          dispatch(setSelectedDeviceIndex(targetDeviceIndex))
          dispatch(setIsDeviceClicked(true))
        }
      } catch (error) {
        console.error(error)
      }
    } else {
      console.warn(messages?.unknownMessageReceived)
    }

    // Close notification menu finally
    handleClose()
  }

  useEffect(() => {
    // Play sound and update notification count
    if (prorithmNotifications?.length > prevNotificationLength?.current) {
      play()
    }
    prevNotificationLength.current = prorithmNotifications?.length || 0

    const unread =
      prorithmNotifications?.filter(
        (notification: ProrithmNotification) => !notification?.isRead
      )?.length || 0
    setUnreadCount(unread)
  }, [prorithmNotifications, play])

  useEffect(() => {
    if (prorithmMessageTypes?.includes(lastMessage?.messageType)) {
      setMessage(lastMessage?.messageContent?.message)
      setOpenSnackbar(true)
    }
  }, [lastMessage])

  return (
    <Box
      display='flex'
      justifyContent='center'
      paddingX='20px'
      marginY={1}
      position='relative'
    >
      {prorithmNotifications?.length > 0 && (
        <>
          <Button
            ref={buttonRef}
            disableElevation
            variant='contained'
            aria-haspopup='true'
            className='alert-button'
            aria-label='Notifications'
            onClick={handleNotificationClick}
            id='prorithm-notifications-button'
            aria-expanded={open ? 'true' : undefined}
            aria-controls={open ? 'prorithm-notifications-menu' : undefined}
          >
            <Badge
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              badgeContent={unreadCount}
              color='error'
            >
              <NotificationsIcon fontSize='medium' />
            </Badge>
          </Button>
          <StyledMenu
            id='prorithm-alerts-menu'
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'prorithm-alert-button',
            }}
          >
            {prorithmNotifications?.map(
              (notification: ProrithmNotification, index: number) => {
                return (
                  <NotificationItem
                    key={notification?.id}
                    notification={notification}
                    index={index}
                    handleNotificationItemClick={handleNotificationItemClick}
                    removeNotification={removeNotification}
                  />
                )
              }
            )}
          </StyledMenu>
        </>
      )}
      <Snackbar
        message={message}
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
        action={action(handleNotificationClose)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  )
}

export default ProrithmNotifications
